import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { PredefinedItem, PredefinedItemData, PredefinedItemType, ShopTypeLabels } from "@autorepair/contracts";
import { predefinedItemsApi, tenantSettingsApi, ApiError } from "../lib/api";
import { formatMoney, parseMoneyInput } from "../lib/money";
import { useNotice } from "../lib/useNotice";
import { useShopType, useShopTypeLabels } from "../lib/theme";
import { describeError } from "../lib/errorCode";
import { Spinner } from "../components/ui-modern";
import {
  Eyebrow,
  ModernButton,
  ModernCard,
  ModernCheckboxRow,
  ModernConfirmButton,
  ModernErrorBanner,
  ModernField,
  ModernInput,
  ModernModal,
  ModernNoticeBanner,
  ModernSelect,
  ModernTextarea,
} from "../components/ui-modern";

/** part/labor/sublet stay the catalog's own internal `type` values (see
 * predefined-items.ts's own doc comment on why — nothing in this codebase
 * writes product/package/service) — only the DISPLAYED name changes per
 * shop type, same "relabel, don't restructure" rule as everywhere else. */
function typeLabels(labels: ShopTypeLabels): Record<PredefinedItemType, string> {
  return { part: labels.partLabel, labor: labels.laborLabel, sublet: labels.subletLabel };
}

/** The tax / shop-supplies / state-inspection flags of an item, as stored in `data`. */
type ItemFlagData = Pick<PredefinedItemData, "taxExempt" | "noShopSupplies" | "stateInspection">;

/** A state inspection implies tax exempt + no shop supplies, so it's the only flag stored then. */
function buildFlagData(f: {
  type: PredefinedItemType;
  taxApplies: boolean;
  taxExempt: boolean;
  noShopSupplies: boolean;
  stateInspection: boolean;
}): ItemFlagData {
  if (f.type === "part" && f.stateInspection) return { stateInspection: true };
  return {
    taxExempt: f.taxApplies && f.taxExempt ? true : undefined,
    noShopSupplies: f.type === "part" && f.noShopSupplies ? true : undefined,
  };
}

/** Checkboxes under an item's Price: tax exempt, no shop supplies, state inspection. */
function ItemFlagsFields({
  type,
  taxApplies,
  shopSuppliesOn,
  inspectionAllowed,
  taxExempt,
  setTaxExempt,
  noShopSupplies,
  setNoShopSupplies,
  stateInspection,
  setStateInspection,
}: {
  type: PredefinedItemType;
  taxApplies: boolean;
  shopSuppliesOn: boolean;
  inspectionAllowed: boolean;
  taxExempt: boolean;
  setTaxExempt: (v: boolean) => void;
  noShopSupplies: boolean;
  setNoShopSupplies: (v: boolean) => void;
  stateInspection: boolean;
  setStateInspection: (v: boolean) => void;
}) {
  const isPart = type === "part";
  const inspecting = isPart && inspectionAllowed && stateInspection;
  return (
    <>
      {isPart && inspectionAllowed && (
        <ModernCheckboxRow className="mt-1.5" checked={stateInspection} onChange={setStateInspection}>
          This is a state inspection
        </ModernCheckboxRow>
      )}
      {inspecting && (
        <p className="m-0 mt-1 text-[12px] text-[#6b7a8f]">Always tax exempt and never charged shop supplies. Result and VIR # are recorded on the work order.</p>
      )}
      {!inspecting && taxApplies && (
        <ModernCheckboxRow className="mt-1.5" checked={taxExempt} onChange={setTaxExempt}>
          Tax exempt
        </ModernCheckboxRow>
      )}
      {!inspecting && isPart && shopSuppliesOn && (
        <ModernCheckboxRow className="mt-1.5" checked={noShopSupplies} onChange={setNoShopSupplies}>
          Don't charge shop supplies on this item
        </ModernCheckboxRow>
      )}
    </>
  );
}

/** Edits an existing item's `data` — same per-type field set as the "Add an
 * item" form above (type itself is immutable, see the contracts' own doc
 * comment, so it's shown but not editable here). */
function EditPredefinedItemModal({
  item,
  showTaxExempt,
  shopSuppliesOn,
  inspectionAllowed,
  onClose,
  onSave,
}: {
  item: PredefinedItem;
  showTaxExempt: boolean;
  shopSuppliesOn: boolean;
  inspectionAllowed: boolean;
  onClose: () => void;
  onSave: (data: PredefinedItemData) => Promise<void>;
}) {
  const [partNumber, setPartNumber] = useState(item.data.partNumber ?? "");
  const [description, setDescription] = useState(item.data.description);
  const [vendorName, setVendorName] = useState(item.data.vendorName ?? "");
  const [price, setPrice] = useState(item.data.price ?? "");
  const [taxExempt, setTaxExempt] = useState(item.data.taxExempt === true);
  const [noShopSupplies, setNoShopSupplies] = useState(item.data.noShopSupplies === true);
  const [stateInspection, setStateInspection] = useState(item.data.stateInspection === true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const TYPE_LABELS = typeLabels(useShopTypeLabels());

  async function save(): Promise<void> {
    if (!description.trim()) return setError("Description is required.");
    const trimmedPrice = price.trim() ? parseMoneyInput(price) : null;
    if (price.trim() && !trimmedPrice) return setError('Price should look like "42.50".');
    setError(null);
    setSaving(true);
    try {
      await onSave({
        partNumber: item.type === "part" && partNumber.trim() ? partNumber.trim() : undefined,
        description: description.trim(),
        vendorName: item.type === "sublet" && vendorName.trim() ? vendorName.trim() : undefined,
        price: trimmedPrice ?? undefined,
        ...buildFlagData({ type: item.type, taxApplies: showTaxExempt, taxExempt, noShopSupplies, stateInspection: inspectionAllowed && stateInspection }),
      });
      onClose();
    } catch {
      setError("Could not save this item. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModernModal title={`Edit ${TYPE_LABELS[item.type]}`} onCancel={onClose}>
      {error && <ModernErrorBanner message={error} />}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        {item.type === "part" && (
          <ModernField label={`${TYPE_LABELS.part} Title`} htmlFor="edit-pi-partNumber">
            <ModernInput id="edit-pi-partNumber" uppercase value={partNumber} onChange={(e) => setPartNumber(e.target.value)} />
          </ModernField>
        )}
        <ModernField label="Description" htmlFor="edit-pi-description" required>
          <ModernTextarea id="edit-pi-description" required uppercase rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </ModernField>
        {item.type === "sublet" && (
          <ModernField label="Vendor" htmlFor="edit-pi-vendor">
            <ModernInput id="edit-pi-vendor" placeholder="Optional" value={vendorName} onChange={(e) => setVendorName(e.target.value)} />
          </ModernField>
        )}
        <ModernField label="Price" htmlFor="edit-pi-price">
          <ModernInput id="edit-pi-price" decimal placeholder="Optional" value={price} onChange={(e) => setPrice(e.target.value)} />
          <ItemFlagsFields
            type={item.type}
            taxApplies={showTaxExempt}
            shopSuppliesOn={shopSuppliesOn}
            inspectionAllowed={inspectionAllowed}
            taxExempt={taxExempt}
            setTaxExempt={setTaxExempt}
            noShopSupplies={noShopSupplies}
            setNoShopSupplies={setNoShopSupplies}
            stateInspection={stateInspection}
            setStateInspection={setStateInspection}
          />
        </ModernField>
        <div className="flex gap-2.5">
          <ModernButton type="submit" disabled={saving || !description.trim()}>
            {saving ? "Saving…" : "Save"}
          </ModernButton>
          <ModernButton type="button" variant="secondary" onClick={onClose}>
            Cancel
          </ModernButton>
        </div>
      </form>
    </ModernModal>
  );
}

/**
 * Settings > Predefined Items — a shop owner's reusable catalog of Part/
 * Labor/Sublet lines, picked from (not required) when adding a line to a
 * work order (see AddPartLineModal/AddLaborLineModal/AddSubletLineModal in
 * WorkOrderEditorPageModern.tsx). No Classic counterpart exists or is
 * planned — this app only builds new work in the modern theme (Classic is
 * being sunset), so this page is linked/routed directly, not behind a
 * theme switch like SettingsPage/AdminPage/TechniciansListPage are.
 *
 * One inline add-form (AdminPageModern's "form above a list" layout) whose
 * fields swap to match the selected type, feeding a single table grouped
 * into Parts/Labor/Sublet sections — the same group-header-row table
 * WorkOrderEditorPageModern's own "Job lines" card uses, reused here so a
 * shop owner sees the same table shape they'll later pick from.
 */
export function PredefinedItemsPageModern() {
  const queryClient = useQueryClient();
  const [showInactive, setShowInactive] = useState(false);
  const TYPE_LABELS = typeLabels(useShopTypeLabels());

  // The "Tax exempt" box only makes sense where the shop charges tax on that
  // type (same rule as the work order's Add-line modals).
  const tenantSettingsQuery = useQuery({ queryKey: ["tenant-settings"], queryFn: tenantSettingsApi.get });
  const shopSuppliesOn = tenantSettingsQuery.data?.settings.shopSuppliesEnabled !== false;
  // State inspections are an auto-repair thing (a detailing shop has none).
  const inspectionAllowed = useShopType() !== "detailing";
  function taxApplies(t: PredefinedItemType): boolean {
    const s = tenantSettingsQuery.data?.settings;
    if (!s) return false;
    return Number(t === "part" ? s.partsTaxRate : t === "labor" ? s.laborTaxRate : s.subletTaxRate) > 0;
  }

  const { data, isLoading, error } = useQuery({
    queryKey: ["predefined-items", showInactive],
    queryFn: () => predefinedItemsApi.list({ includeInactive: showInactive, limit: 100 }),
  });

  // Column-header sort — a display preference only, not persisted and not
  // reflected in the work order picker's own order (that stays server-side,
  // newest-first). Sorted independently within each Parts/Labor/Sublet
  // group, not across them, since the groups are a fixed partition already.
  const [sortField, setSortField] = useState<"item" | "price" | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  function toggleSort(field: "item" | "price"): void {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  }

  // Whichever field renders as this row's bold, primary title in
  // renderGroup below — partNumber for a part, vendorName for a sublet,
  // description for labor (its only field) — mirrors the same
  // COALESCE the backend's default ordering uses, so "sort by item"
  // matches what's actually visible instead of always comparing
  // description even when a different field is shown more prominently.
  function itemTitle(item: PredefinedItem): string {
    return item.data.partNumber ?? item.data.vendorName ?? item.data.description;
  }

  function sortItems(items: PredefinedItem[]): PredefinedItem[] {
    if (!sortField) return items;
    const sign = sortDir === "asc" ? 1 : -1;
    return [...items].sort((a, b) => {
      if (sortField === "item") return sign * itemTitle(a).localeCompare(itemTitle(b));
      const priceA = a.data.price ? Number(a.data.price) : -Infinity;
      const priceB = b.data.price ? Number(b.data.price) : -Infinity;
      return sign * (priceA - priceB);
    });
  }

  const partItems = sortItems(data?.items.filter((i) => i.type === "part") ?? []);
  const laborItems = sortItems(data?.items.filter((i) => i.type === "labor") ?? []);
  const subletItems = sortItems(data?.items.filter((i) => i.type === "sublet") ?? []);

  const [type, setType] = useState<PredefinedItemType>("part");
  const [partNumber, setPartNumber] = useState("");
  const [description, setDescription] = useState("");
  const [vendorName, setVendorName] = useState("");
  const [price, setPrice] = useState("");
  const [taxExempt, setTaxExempt] = useState(false);
  const [noShopSupplies, setNoShopSupplies] = useState(false);
  const [stateInspection, setStateInspection] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const listNotice = useNotice();

  function resetForm(): void {
    setPartNumber("");
    setDescription("");
    setVendorName("");
    setPrice("");
    setTaxExempt(false);
    setNoShopSupplies(false);
    setStateInspection(false);
  }

  const createMutation = useMutation({
    mutationFn: () => {
      const trimmedPrice = price.trim() ? parseMoneyInput(price) : null;
      if (price.trim() && !trimmedPrice) throw new Error('Price should look like "42.50".');
      return predefinedItemsApi.create({
        type,
        data: {
          description: description.trim(),
          partNumber: type === "part" && partNumber.trim() ? partNumber.trim() : undefined,
          vendorName: type === "sublet" && vendorName.trim() ? vendorName.trim() : undefined,
          price: trimmedPrice ?? undefined,
          ...buildFlagData({ type, taxApplies: taxApplies(type), taxExempt, noShopSupplies, stateInspection: inspectionAllowed && stateInspection }),
        },
      });
    },
    onSuccess: () => {
      resetForm();
      setFormError(null);
      void queryClient.invalidateQueries({ queryKey: ["predefined-items"] });
    },
    onError: (err) => setFormError(err instanceof ApiError || err instanceof Error ? err.message : "Could not add this item."),
  });

  const toggleActiveMutation = useMutation({
    mutationFn: (item: PredefinedItem) => predefinedItemsApi.update(item.id, { isActive: !item.isActive }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["predefined-items"] }),
    onError: (err) => listNotice.showError(err instanceof ApiError ? err.message : "Could not update this item."),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => predefinedItemsApi.delete(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["predefined-items"] }),
    onError: (err) => listNotice.showError(err instanceof ApiError ? err.message : "Could not delete this item."),
  });

  const [editingItem, setEditingItem] = useState<PredefinedItem | null>(null);
  const updateDataMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: PredefinedItemData }) =>
      predefinedItemsApi.update(id, { data }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["predefined-items"] }),
  });

  function renderGroup(label: string, items: PredefinedItem[], showColumn: "partNumber" | "vendorName" | null) {
    return (
      <>
        <tr>
          <td
            colSpan={3}
            className="border-b border-[#eef1f6] bg-[#f8fafc] px-[18px] pb-2 pt-3 font-mono text-[13px] font-bold uppercase tracking-[.08em] text-[#071a33]"
          >
            {label}
          </td>
        </tr>
        {items.map((item) => (
          <tr key={item.id}>
            <td className="border-b border-[#f1f4f9] px-[18px] py-3">
              {showColumn && item.data[showColumn] && (
                <div className="font-mono text-[14px] font-semibold text-[#101b2d]">{item.data[showColumn]}</div>
              )}
              <div className={`text-[14px] ${item.isActive ? "text-[#101b2d]" : "text-[#8b97a8]"}`}>
                {item.data.description}
                {!item.isActive && " (inactive)"}
              </div>
              {item.data.stateInspection ? (
                <div className="text-[11px] font-semibold text-[#0f6d86]">State inspection · tax exempt · no shop supplies</div>
              ) : (
                <>
                  {item.data.taxExempt && <div className="text-[11px] text-[#8b97a8]">Tax exempt</div>}
                  {item.data.noShopSupplies && <div className="text-[11px] text-[#8b97a8]">No shop supplies</div>}
                </>
              )}
            </td>
            <td className="border-b border-[#f1f4f9] px-2 py-3 text-right tabular-nums text-[#4a5361]">
              {item.data.price ? formatMoney(item.data.price) : "—"}
            </td>
            <td className="whitespace-nowrap border-b border-[#f1f4f9] py-3 pl-3 pr-[18px] text-right">
              <button
                type="button"
                className="p-1.5 text-[13px] font-semibold text-[#0f6d86] disabled:opacity-50"
                onClick={() => setEditingItem(item)}
              >
                Edit
              </button>
              <button
                type="button"
                className="p-1.5 text-[13px] font-semibold text-[#0f6d86] disabled:opacity-50"
                disabled={toggleActiveMutation.isPending}
                onClick={() => toggleActiveMutation.mutate(item)}
              >
                {item.isActive ? "Deactivate" : "Activate"}
              </button>
              <ModernConfirmButton
                variant="danger"
                className="min-h-0 border-0 p-1.5 text-[13px] font-semibold"
                confirmText="Permanently delete this predefined item?"
                disabled={deleteMutation.isPending}
                onConfirm={() => deleteMutation.mutate(item.id)}
              >
                Delete
              </ModernConfirmButton>
            </td>
          </tr>
        ))}
        {items.length === 0 && (
          <tr>
            <td colSpan={3} className="border-b border-[#f1f4f9] px-[18px] py-3 text-[13.5px] text-[#8b97a8]">
              None yet.
            </td>
          </tr>
        )}
      </>
    );
  }

  return (
    <div>
      <div className="mb-5">
        <Eyebrow className="mb-2 text-[11px] text-[#0f6d86]">Settings</Eyebrow>
        <h1 className="m-0 text-[28px] font-bold text-[#071a33]">Predefined items</h1>
        <p className="m-0 mt-1.5 text-[13.5px] text-[#6b7a8f]">
          Set up {TYPE_LABELS.part.toLowerCase()}, {TYPE_LABELS.labor.toLowerCase()}, and {TYPE_LABELS.sublet.toLowerCase()} lines
          once here, then pick from them when building a work order — typing a line by hand still works exactly the
          same either way.
        </p>
      </div>

      {inspectionAllowed && (
        <details className="mb-4 rounded-[14px] border border-[#e2e7ee] bg-white px-[18px] py-3 text-[13.5px] text-[#4a5361]">
          <summary className="cursor-pointer text-[14px] font-bold text-[#071a33]">How to set up a state inspection item</summary>
          <ol className="m-0 mt-2.5 list-decimal space-y-1 pl-5">
            <li>
              Below, choose type <strong>{TYPE_LABELS.part}</strong>.
            </li>
            <li>
              Description: for example <strong>STATE EMISSIONS INSPECTION</strong>. Enter <strong>INSPECTION</strong> as the {TYPE_LABELS.part.toLowerCase()} title.
            </li>
            <li>
              Price: what your county allows you to charge. In Texas that's up to $18.50 for emissions in the Dallas–Fort Worth and Houston
              counties, $11.50 in El Paso, Travis and Williamson, and up to $40 for a commercial safety inspection.
            </li>
            <li>
              Check <strong>This is a state inspection</strong>. It's automatically tax exempt and never charged shop supplies.
            </li>
            <li>
              Click <strong>Add Item</strong>. When you pick it on a work order, Bay2Bill reminds you to fill in the VIN, plate and mileage, and
              lets you record pass/fail, the VIR # and who did the test. Those appear on the invoice and on the Inspections page.
            </li>
          </ol>
        </details>
      )}

      <ModernCard className="mb-4">
        <h2 className="m-0 mb-3 text-[15px] font-bold text-[#071a33]">Add an item</h2>
        {formError && <ModernErrorBanner message={formError} />}
        <form
          className="max-w-md"
          onSubmit={(e) => {
            e.preventDefault();
            createMutation.mutate();
          }}
        >
          <ModernField label="Type" htmlFor="pi-type">
            <ModernSelect id="pi-type" value={type} onChange={(e) => setType(e.target.value as PredefinedItemType)}>
              {(Object.keys(TYPE_LABELS) as PredefinedItemType[]).map((t) => (
                <option key={t} value={t}>
                  {TYPE_LABELS[t]}
                </option>
              ))}
            </ModernSelect>
          </ModernField>
          {type === "part" && (
            <ModernField label={`${TYPE_LABELS.part} Title`} htmlFor="pi-partNumber">
              <ModernInput id="pi-partNumber" uppercase value={partNumber} onChange={(e) => setPartNumber(e.target.value)} />
            </ModernField>
          )}
          <ModernField label="Description" htmlFor="pi-description" required>
            <ModernTextarea
              id="pi-description"
              required
              uppercase
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </ModernField>
          {type === "sublet" && (
            <ModernField label="Vendor" htmlFor="pi-vendor">
              <ModernInput id="pi-vendor" placeholder="Optional" value={vendorName} onChange={(e) => setVendorName(e.target.value)} />
            </ModernField>
          )}
          <ModernField label="Price" htmlFor="pi-price">
            <ModernInput id="pi-price" decimal placeholder="Optional" value={price} onChange={(e) => setPrice(e.target.value)} />
            <ItemFlagsFields
              type={type}
              taxApplies={taxApplies(type)}
              shopSuppliesOn={shopSuppliesOn}
              inspectionAllowed={inspectionAllowed}
              taxExempt={taxExempt}
              setTaxExempt={setTaxExempt}
              noShopSupplies={noShopSupplies}
              setNoShopSupplies={setNoShopSupplies}
              stateInspection={stateInspection}
              setStateInspection={setStateInspection}
            />
          </ModernField>
          <ModernButton
            type="submit"
                        disabled={createMutation.isPending || !description.trim()}
          >
            {createMutation.isPending ? "Adding…" : "Add Item"}
          </ModernButton>
        </form>
      </ModernCard>

      <label className="mb-4 flex items-center gap-2 text-[13.5px] text-[#4a5361]">
        <input
          type="checkbox"
          checked={showInactive}
          onChange={(e) => setShowInactive(e.target.checked)}
          className="h-4 w-4 rounded border border-[#e2e7ee]"
        />
        Show deactivated items
      </label>

      <ModernNoticeBanner notice={listNotice.notice} />
      {isLoading && <Spinner />}
      {error && <ModernErrorBanner message={describeError("ERR-PI-LOAD", "Could not load predefined items.", error)} />}

      {data && (
        <ModernCard className="min-w-0 p-0">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[500px] border-collapse text-[14px]">
              <thead>
                <tr className="bg-[#f8fafc]">
                  <th className="border-b border-[#eef1f6] px-[18px] pb-2 pt-3 text-left font-mono text-[13px] font-bold uppercase tracking-[.08em] text-[#071a33]">
                    <button type="button" className="flex items-center gap-1 hover:text-[#0f6d86]" onClick={() => toggleSort("item")}>
                      Item {sortField === "item" && (sortDir === "asc" ? "▲" : "▼")}
                    </button>
                  </th>
                  <th className="border-b border-[#eef1f6] px-2 py-2 text-right font-mono text-[11px] font-semibold uppercase tracking-[.1em] text-[#6b7a8f]">
                    <button
                      type="button"
                      className="ml-auto flex items-center gap-1 hover:text-[#0f6d86]"
                      onClick={() => toggleSort("price")}
                    >
                      Price {sortField === "price" && (sortDir === "asc" ? "▲" : "▼")}
                    </button>
                  </th>
                  <th className="border-b border-[#eef1f6] py-2 pl-3 pr-[18px]" />
                </tr>
              </thead>
              <tbody>
                {renderGroup(TYPE_LABELS.part, partItems, "partNumber")}
                {renderGroup(TYPE_LABELS.labor, laborItems, null)}
                {renderGroup(TYPE_LABELS.sublet, subletItems, "vendorName")}
              </tbody>
            </table>
          </div>
        </ModernCard>
      )}

      {editingItem && (
        <EditPredefinedItemModal
          item={editingItem}
          showTaxExempt={taxApplies(editingItem.type)}
          shopSuppliesOn={shopSuppliesOn}
          inspectionAllowed={inspectionAllowed}
          onClose={() => setEditingItem(null)}
          onSave={(data) => updateDataMutation.mutateAsync({ id: editingItem.id, data }).then(() => undefined)}
        />
      )}
    </div>
  );
}
